import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Mail, Lock, User, Building2, Ticket, ArrowRight, CheckCircle2, AlertCircle, Sparkles, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TEAM_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 'request' = public self-serve path (default, low-friction framing)
  // 'invite'  = user has an invite code → instant trial, no approval queue
  const [mode, setMode] = useState('request');

  // Shared fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');

  // Invite-path only
  const [inviteToken, setInviteToken] = useState('');

  // Request-access-path only
  const [roleTitle, setRoleTitle] = useState('');
  const [useCase, setUseCase] = useState('');
  const [teamSize, setTeamSize] = useState('');

  const [submitted, setSubmitted] = useState(null); // null | { pending: boolean, email: string }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill the invite token from the URL: /register?invite=<token>
  // When ?invite= is present, default to invite-mode automatically.
  useEffect(() => {
    const fromQuery = searchParams.get('invite');
    if (fromQuery) {
      setInviteToken(fromQuery);
      setMode('invite');
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { email, password, name, company };
      if (mode === 'invite') {
        payload.invite_token = inviteToken;
      } else {
        payload.role_title = roleTitle;
        payload.use_case = useCase;
        payload.team_size = teamSize;
      }
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      setSubmitted({ pending: data.signup_path === 'request_access', email });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12" style={{ background: 'radial-gradient(ellipse at top, rgba(59, 130, 246, 0.06) 0%, rgba(0, 0, 0, 0) 60%), #0a0a0b' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <BarChart3 className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">INZARA</h1>
        </div>

        <div className="rounded-2xl p-8" style={{ background: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          {submitted ? (
            <div className="text-center" data-testid="register-success">
              {submitted.pending ? (
                <>
                  <Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-xl font-heading font-semibold text-white mb-2">Request received</h2>
                  <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
                    We sent a verification link to <span className="text-white">{submitted.email}</span>.
                  </p>
                  <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                    Click it to confirm your email, then our team will review your access request — usually within 1 business day. You&apos;ll get another email the moment your trial goes live.
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <h2 className="text-xl font-heading font-semibold text-white mb-2">Check your inbox</h2>
                  <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                    We sent a verification link to <span className="text-white">{submitted.email}</span>. Click it to activate your account and start your 14-day trial.
                  </p>
                </>
              )}
              <p className="text-xs text-zinc-500 mb-6">
                Didn&apos;t receive it? Check spam, or{' '}
                <button
                  onClick={async () => {
                    await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: submitted.email }),
                    });
                  }}
                  className="text-blue-400 hover:text-blue-300"
                  data-testid="resend-verification-btn"
                >
                  resend the link
                </button>.
              </p>
              <Button onClick={() => navigate('/login')} variant="outline" className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="back-to-login-btn">
                Back to Sign In
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-heading font-semibold text-white mb-1">Get started with INZARA</h2>
              <p className="text-xs text-zinc-500 mb-5">
                {mode === 'invite' ? 'Have an invite code? Activate your account in minutes.' : 'Tell us a bit about yourself and we\'ll set up your workspace.'}
              </p>

              {/* Mode toggle */}
              <div className="grid grid-cols-2 gap-2 mb-5 p-1 rounded-xl bg-white/5 border border-white/10">
                <button
                  type="button"
                  data-testid="register-mode-invite"
                  onClick={() => setMode('invite')}
                  className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${mode === 'invite' ? 'bg-blue-500 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}
                >
                  <Ticket className="w-3.5 h-3.5" /> I have an invite
                </button>
                <button
                  type="button"
                  data-testid="register-mode-request"
                  onClick={() => setMode('request')}
                  className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${mode === 'request' ? 'bg-blue-500 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}
                >
                  <Sparkles className="w-3.5 h-3.5" /> Get started
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-300 leading-relaxed">{error}</p>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-3.5">
                {mode === 'invite' && (
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs">Invite code</Label>
                    <div className="relative">
                      <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        data-testid="register-invite"
                        placeholder="Paste your invite code"
                        value={inviteToken}
                        onChange={e => setInviteToken(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl font-mono text-xs"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs">Full name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        data-testid="register-name"
                        placeholder="Jane Doe"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs">Company</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        data-testid="register-company"
                        placeholder="Acme Co"
                        value={company}
                        onChange={e => setCompany(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                        required={mode === 'request'}
                      />
                    </div>
                  </div>
                </div>

                {mode === 'request' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-zinc-300 text-xs">Your role</Label>
                        <Input
                          data-testid="register-role"
                          placeholder="VP Analytics"
                          value={roleTitle}
                          onChange={e => setRoleTitle(e.target.value)}
                          className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-300 text-xs">Team size</Label>
                        <Select value={teamSize} onValueChange={setTeamSize}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="register-teamsize">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            {TEAM_SIZES.map(s => (
                              <SelectItem key={s} value={s} data-testid={`teamsize-${s}`}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-zinc-300 text-xs">What are you trying to do?</Label>
                      <Textarea
                        data-testid="register-usecase"
                        placeholder="e.g. Replace our stale Tableau dashboards with AI-driven insights for our RevOps team"
                        value={useCase}
                        onChange={e => setUseCase(e.target.value)}
                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl min-h-[64px] resize-none"
                        maxLength={500}
                        required
                      />
                      <p className="text-[10px] text-zinc-500">One sentence is plenty — we just want to know you&apos;re a real buyer, not a bot.</p>
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">Work email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="register-email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="register-password"
                      type="password"
                      placeholder="At least 10 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                      required
                    />
                  </div>
                  <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Must be 10+ characters with upper, lower, digit, and symbol. Won&apos;t match common passwords.
                  </p>
                </div>

                <Button
                  data-testid="register-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all duration-300 active:scale-95"
                >
                  {loading ? (mode === 'invite' ? 'Creating account…' : 'Creating your account…') : (mode === 'invite' ? 'Start 14-day trial' : 'Get started')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-center text-xs text-zinc-500 pt-2">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                    data-testid="register-login-link"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
